import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Megaphone, Calendar, ArrowRight, Bell, X } from 'lucide-react';
import { fetchAnnouncements } from '../../../lib/announcementService';

const AnnouncementsSection = () => {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);

  useEffect(() => {
    let isMounted = true;
    
    const loadData = async () => {
      try {
        const data = await fetchAnnouncements();
        // Take only the top 3 for the homepage display
        if (isMounted) {
          setAnnouncements(data.slice(0, 3));
          setLoading(false);
        }
      } catch (error) {
        console.error("Failed to load announcements section", error);
        if (isMounted) setLoading(false);
      }
    };

    loadData();

    return () => { isMounted = false; };
  }, []);

  // Prevent background scrolling when modal is open
  useEffect(() => {
    if (selectedAnnouncement) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => { document.body.style.overflow = 'unset'; };
  }, [selectedAnnouncement]);

  const getExcerpt = (htmlContent, length = 100) => {
    const tmp = document.createElement('DIV');
    tmp.innerHTML = htmlContent || '';
    const text = tmp.textContent || tmp.innerText || '';
    return text.length > length ? text.substring(0, length) + '...' : text;
  };

  if (!loading && announcements.length === 0) {
    return null;
  }

  return (
    <>
      <section className="py-16 bg-white dark:bg-gray-900 transition-colors duration-300">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Section Header */}
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-4">
            <div>
              <div className="flex items-center gap-2 text-blue-600 dark:text-blue-400 font-semibold mb-2">
                <Bell className="w-5 h-5" />
                <span className="uppercase tracking-wider text-sm">Updates & News</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 dark:text-white leading-tight">
                Latest from the Park
              </h2>
            </div>
            <Link 
              to="/announcements" 
              className="group flex items-center gap-2 text-blue-600 dark:text-blue-400 font-medium hover:text-blue-700 dark:hover:text-blue-300 transition-colors"
            >
              View all announcements
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </div>

          {/* Content Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {loading ? (
              // Skeleton Loaders
              [...Array(3)].map((_, i) => (
                <div key={i} className="bg-gray-100 dark:bg-gray-800 rounded-2xl p-6 h-64 animate-pulse">
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/3 mb-4"></div>
                  <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-4"></div>
                  <div className="space-y-2">
                    <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-full"></div>
                    <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-5/6"></div>
                  </div>
                </div>
              ))
            ) : (
              // Live Cards (Now Clickable Divs)
              announcements.map((item) => (
                <div 
                  key={item.id} 
                  onClick={() => setSelectedAnnouncement(item)}
                  className="group flex flex-col bg-gray-50 dark:bg-gray-800 rounded-2xl p-6 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 border border-gray-100 dark:border-gray-700 cursor-pointer"
                >
                  <div className="flex items-center gap-2 text-xs font-medium text-gray-500 dark:text-gray-400 mb-4">
                    <Calendar className="w-4 h-4" />
                    <span>
                      {item.createdAt 
                        ? new Date(item.createdAt).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' }) 
                        : 'Recent'}
                    </span>
                  </div>

                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors line-clamp-2">
                    {item.title}
                  </h3>

                  <p className="text-gray-600 dark:text-gray-300 text-sm leading-relaxed mb-6 line-clamp-3 flex-grow">
                    {getExcerpt(item.description)}
                  </p>

                  <div className="flex items-center gap-2 text-blue-600 dark:text-blue-400 text-sm font-semibold mt-auto">
                    Read more
                    <ArrowRight className="w-4 h-4 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300" />
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </section>

      {/* Detail Modal */}
      {selectedAnnouncement && (
        <div 
          className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-in fade-in duration-200"
          onClick={() => setSelectedAnnouncement(null)}
        >
          <div 
            className="relative w-full max-w-5xl bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-h-[90vh] flex flex-col overflow-hidden animate-in zoom-in-95 duration-200"
            onClick={e => e.stopPropagation()}
          >
            {/* Header */}
            <div className="bg-blue-600 text-white px-6 py-4 flex items-center justify-between shrink-0">
              <div className="flex items-center gap-3">
                <Megaphone className="h-6 w-6" />
                <h3 className="text-lg font-bold">Announcement Detail</h3>
              </div>
              <button
                onClick={() => setSelectedAnnouncement(null)}
                className="text-white hover:bg-blue-700 rounded-full p-1 transition-colors"
                aria-label="Close modal"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            {/* Content Scroll Area */}
            <div className="p-6 overflow-y-auto">
              <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 mb-4">
                <Calendar className="w-4 h-4" />
                <span>
                  {selectedAnnouncement.createdAt 
                    ? new Date(selectedAnnouncement.createdAt).toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }) 
                    : 'Recent'}
                </span>
              </div>

              <h4 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
                {selectedAnnouncement.title}
              </h4>
              
              <div 
                className="text-base text-gray-700 dark:text-gray-300 prose prose-blue dark:prose-invert max-w-none"
                dangerouslySetInnerHTML={{ __html: selectedAnnouncement.description }}
              />
            </div>
            
            {/* Footer / Close Action */}
            <div className="p-4 border-t border-gray-100 dark:border-gray-700 flex justify-end bg-gray-50 dark:bg-gray-800/50">
               <button 
                 onClick={() => setSelectedAnnouncement(null)}
                 className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-white rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors font-medium text-sm"
               >
                 Close
               </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AnnouncementsSection;